import * as types from '../ActionTypes/ClassesActionTypes';
import { getFormattedDateFromTimestamp } from '../../Utils/timeConvertorHelper';
import { timeFormats } from '../../Constants/timeFormats';
import { getClassModelsArray, getClassMemberModelsArray, getClassBookerModelsArray } from './helpers';

const initialState = {
    statuses: {
        classesLoading: false,
        moreClassesLoading: false,
        isClassCreating: false,
        isClassUpdating: false,
        isClassDeleting: false,
        isClassSeriesDeleting: false,

        // class members and bookers
        classBookersLoading: false,
        classMembersLoading: false,
        isCheckingIn: {
            isCheckingIn: false,
            checkingInId: null,
        },
        isCheckingOut: {
            isCheckingOut: false,
            checkingOutId: null,
        },
        classBooking: {
            isClassBooking: false,
            bookingClassId: null,
        },
        classBookingCancelling: {
            isClassBookingCancelling: false,
            bookingCancellingId: null,
        },
    },
    classes: [],
    classesByDate: [],
    currentClass: {
        members: [],
        bookers: [],
    },
    offsetDate: null,
    offsetId: null,
    moreClassesToLoad: true,
    errs: {},
};

export function ClassesReducer(state = initialState, { type, payload, meta, ...actions }) {
    switch (type) {
        case types.CLEAR_CLASS_ERRORS: {
            return {
                ...state,
                errs: {},
            };
        }

        case types.GET_CLASSES_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classesLoading: true,
                },
            };
        }

        case types.GET_CLASSES_SUCCESS: {
            const classesArray = getClassModelsArray(payload.classes);
            const classesByDate = sortClassesByDate(classesArray);

            return {
                ...state,
                classes: [...classesArray],
                classesByDate: [...classesByDate],
                offsetId: payload.offsetId,
                offsetDate: payload.offsetDate,
                statuses: {
                    ...state.statuses,
                    classesLoading: false,
                },
            };
        }

        case types.GET_CLASSES_FAILED: {
            return {
                ...state,
                errs: payload,
            };
        }

        case types.GET_CLASSES_BY_DATE_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classesLoading: true,
                },
            };
        }

        case types.GET_CLASSES_BY_DATE_SUCCESS: {
            const classesArray = getClassModelsArray(payload);

            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classesLoading: false,
                },
                classes: [...classesArray],
            };
        }

        case types.LOAD_MORE_CLASSES_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    additionalClassesLoading: true,
                },
            };
        }

        case types.LOAD_MORE_CLASSES_SUCCESS: {
            const moreClassesToLoad = payload.classes.length > 0;

            const classesArray = getClassModelsArray(payload.classes);
            const classesByDate = sortClassesByDate(classesArray);

            return {
                ...state,
                classes: [...state.classes, ...classesArray],
                classesByDate: [...state.classesByDate, ...classesByDate],
                offsetId: payload.offsetId,
                offsetDate: payload.offsetDate,
                moreClassesToLoad,
                statuses: {
                    ...state.statuses,
                    additionalClassesLoading: false,
                },
            };
        }

        case types.CREATE_CLASS_REQUEST: {
            return {
                ...state,
                errs: {},
                statuses: {
                    ...state.statuses,
                    isClassCreating: true,
                },
            };
        }

        case types.CREATE_CLASS_SUCCESS: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    // TODO: update current list of classes
                    isClassCreating: false,
                },
            };
        }

        case types.CREATE_CLASS_FAILED: {
            // const { free_cancel_date, ...restErrors } = payload;

            return {
                ...state,
                errs: payload,
                statuses: {
                    ...state.statuses,
                    isClassCreating: false,
                },
            };
        }

        case types.UPDATE_CLASS_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isClassUpdating: true,
                },
            };
        }

        case types.UPDATE_CLASS_SUCCESS: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    // TODO: update current class
                    isClassUpdating: false,
                },
            };
        }

        case types.UPDATE_CLASS_FAILED: {
            const { free_cancel_date, ...restErrors } = payload;

            return {
                ...state,
                errs: restErrors,
                statuses: {
                    ...state.statuses,
                    isClassUpdating: false,
                },
            };
        }

        case types.DELETE_CLASS_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isClassDeleting: true,
                },
            };
        }

        case types.DELETE_CLASS_SUCCESS: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isClassDeleting: false,
                },
            };
        }

        case types.DELETE_CLASS_FAILED: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isClassDeleting: false,
                },
            };
        }

        case types.DELETE_CLASS_SERIES_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isClassSeriesDeleting: true,
                },
            };
        }

        case types.DELETE_CLASS_SERIES_SUCCESS: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isClassSeriesDeleting: false,
                },
            };
        }

        case types.DELETE_CLASS_SERIES_FAILED: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isClassSeriesDeleting: true,
                },
            };
        }

        case types.GET_CLASS_BOOKERS_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classBookersLoading: true,
                },
            };
        }

        case types.GET_CLASS_BOOKERS_SUCCESS: {
            const classBookersArray = getClassBookerModelsArray(payload);

            return {
                ...state,
                currentClass: {
                    ...state.currentClass,
                    bookers: [...classBookersArray],
                },
                statuses: {
                    ...state.statuses,
                    classBookersLoading: false,
                },
            };
        }

        case types.GET_CLASS_MEMBERS_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classMembersLoading: true,
                },
            };
        }

        case types.GET_CLASS_MEMBERS_SUCCESS: {
            const classMembersArray = getClassMemberModelsArray(payload);

            return {
                ...state,
                currentClass: {
                    ...state.currentClass,
                    members: [...classMembersArray],
                },
                statuses: {
                    ...state.statuses,
                    classMembersLoading: false,
                },
            };
        }

        case types.CHECK_IN_USER_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isCheckingIn: {
                        ...state.isCheckingIn,
                        isCheckingIn: true,
                        checkingInId: meta.userId,
                    },
                },
            };
        }

        case types.CHECK_IN_USER_SUCCESS: {
            let newMembersList = state.currentClass.members.map(member => {
                if (member.id === payload) {
                    member.isChecked = true;
                    return member;
                }

                return member;
            });

            let newBookersList = state.currentClass.bookers.map(booker => {
                if (booker.id === payload) {
                    booker.isChecked = true;
                    return booker;
                }

                return booker;
            });

            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isCheckingIn: {
                        ...state.isCheckingIn,
                        isCheckingIn: false,
                        checkingInId: '',
                    },
                },
                currentClass: {
                    ...state.currentClass,
                    bookers: newBookersList,
                    members: newMembersList,
                },
            };
        }

        case types.CHECK_OUT_USER_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isCheckingOut: {
                        ...state.isCheckingOut,
                        isCheckingOut: true,
                        checkingOutId: meta.userId,
                    },
                },
            };
        }

        case types.CHECK_OUT_USER_SUCCESS: {
            let newMembersList = state.currentClass.members.map(member => {
                if (member.id === payload) {
                    member.isChecked = false;
                    return member;
                }

                return member;
            });

            let newBookersList = state.currentClass.bookers.map(booker => {
                if (booker.id === payload) {
                    booker.isChecked = false;
                    return booker;
                }

                return booker;
            });

            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    isCheckingOut: {
                        ...state.isCheckingOut,
                        isCheckingOut: false,
                        checkingOutId: '',
                    },
                },
                currentClass: {
                    ...state.currentClass,
                    bookers: newBookersList,
                    members: newMembersList,
                },
            };
        }

        case types.GET_CLASSES_BY_DATE_RANGE_REQUEST: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classesLoading: true,
                },
            };
        }

        case types.GET_CLASSES_BY_DATE_RANGE_SUCCESS: {
            const classesArray = getClassModelsArray(payload);

            return {
                ...state,
                classes: [...classesArray],
                statuses: {
                    ...state.statuses,
                    classesLoading: false,
                },
            };
        }

        case types.BOOK_CLASS_REQUEST: {
            const { classId } = meta;
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classBooking: {
                        ...state.classBooking,
                        isClassBooking: true,
                        bookingClassId: classId,
                    },
                },
            };
        }

        case types.BOOK_CLASS_SUCCESS: {
            const newClassesList = [...state.classes];
            const newClassesByDateList = [...state.classesByDate];

            newClassesByDateList.forEach(date => {
                date.classes.forEach(_class => {
                    if (_class.id === payload.id) _class.isBooked = true;
                });
            });

            newClassesList.forEach(_class => {
                if (_class.id === payload.id) _class.isBooked = true;
            });

            return {
                ...state,
                classes: [...newClassesList],
                classesByDate: [...newClassesByDateList],
                statuses: {
                    ...state.statuses,
                    classBooking: {
                        // ...state.classBooking,
                        isClassBooking: false,
                        bookingClassId: 0,
                    },
                },
            };
        }

        case types.BOOK_CLASS_FAILED: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classBooking: {
                        isClassBooking: false,
                        bookingClassId: 0,
                    },
                },
            };
        }

        case types.CANCEL_CLASS_BOOKING_REQUEST: {
            const { classId } = meta;
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classBookingCancelling: {
                        isClassBookingCancelling: true,
                        bookingCancellingId: classId,
                    },
                },
            };
        }

        case types.CANCEL_CLASS_BOOKING_SUCCESS: {
            const newClassesList = [...state.classes];
            const newClassesByDateList = [...state.classesByDate];

            newClassesByDateList.forEach(date => {
                date.classes.forEach(_class => {
                    if (_class.id === payload.id) _class.isBooked = false;
                });
            });

            newClassesList.forEach(_class => {
                if (_class.id === payload.id) _class.isBooked = true;
            });

            return {
                ...state,
                classes: [...newClassesList],
                classesByDate: [...newClassesByDateList],
                statuses: {
                    ...state.statuses,
                    classBookingCancelling: {
                        isClassBookingCancelling: false,
                        bookingCancellingId: 0,
                    },
                },
            };
        }

        case types.CANCEL_CLASS_BOOKING_FAILED: {
            return {
                ...state,
                statuses: {
                    ...state.statuses,
                    classBookingCancelling: {
                        isClassBookingCancelling: false,
                        bookingCancellingId: 0,
                    },
                },
            };
        }

        default: {
            return state;
        }
    }
}

const sortClassesByDate = classes => {
    let result = [];
    let dateClasses = {};

    if (classes.length > 0) {
        dateClasses.date = getFormattedDateFromTimestamp(
            classes[0].start,
            timeFormats.FULL_DAY_NAME_MON_DAY_ORDER_YEAR,
        );
        dateClasses.classes = [];

        classes.forEach((_class, index) => {
            let classDate = getFormattedDateFromTimestamp(_class.start, timeFormats.FULL_DAY_NAME_MON_DAY_ORDER_YEAR);

            if (classDate === dateClasses.date) {
                dateClasses.classes.push(_class);
            } else {
                result.push(dateClasses);
                dateClasses = {};
                dateClasses.date = classDate;
                dateClasses.classes = [];
                dateClasses.classes.push(_class);
            }

            if (index === classes.length - 1) {
                result.push(dateClasses);
            }
        });
    }

    return result;
};



// WEBPACK FOOTER //
// ./src/Store/Reducers/ClassesReducer.js